"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRightIcon, CaretRightIcon } from "@phosphor-icons/react/ssr";

import { cn } from "~/lib/utils";
import Heading from "~/componentsDS/Typography/Heading";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { useQuizLink } from "~/components/features/quiz";
import { HeroProduct } from "./data";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";

type ProductCardProps = {
    product: HeroProduct;
};

export const ProductCard = ({ product }: ProductCardProps) => {
    const isFeatured = product.variant === "featured";
    const { href } = useQuizLink(product.href);
    const sendEvent = useTrackEvent();

    const handleClick = () => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "hero",
                meta_data: {
                    click_text: product.eyebrow,
                    product_name: product.product
                },
            },
        });
    };

    return (
        <Link
            className={cn(
                "group relative flex min-w-0 items-center overflow-hidden rounded-[16px]",
                "bg-trueBlack/25 text-white backdrop-blur-[50px]",
                "transition-colors hover:bg-trueBlack/50",
            )}
            href={href}
            onClick={handleClick}
        >
            <div className={cn(
                "flex w-[91px] shrink-0 items-center justify-center max-[389px]:w-[82px]",
                isFeatured ? "xl:w-[clamp(64px,7.4vw,112px)]" : "xl:w-[clamp(64px,7.4vw,72px)]"
            )}>
                <div
                    className={cn(
                        isFeatured
                            ? "h-[137px] w-[114px]"
                            : "h-[96px] w-[80px]"
                    )}
                >
                    <Image
                        src={product.image}
                        alt={product.imageAlt}
                        width={product.imageWidth}
                        height={product.imageHeight}
                        sizes={isFeatured ? "228px" : "160px"}
                        loading="lazy"
                        className="h-full w-full object-contain transition-transform duration-200 ease-[cubic-bezier(.4,0,.2,1)] group-hover:scale-[1.1] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                    />
                </div>
            </div>

            <div className="min-w-0 self-center pr-10 xl:pr-7">
                <Heading as="p" size="2xs" weight="400" className={cn(
                    "mb-1.5 xl:mb-2",
                    isFeatured && "xl:hidden"
                )}>
                    {product.eyebrow}
                </Heading>
                <div className="max-w-[267px]">
                    <Heading
                        as="span"
                        size="3xs"
                        weight="500"
                        className={cn(
                            "inline max-md:text-[18px]",
                            isFeatured && "xl:text-[24px] xl:font-normal"
                        )}
                    >
                        <TextWithRegisteredMark text={product.name} />
                    </Heading>
                    {" "}
                    <Heading
                        font="featureDisplay"
                        as="i" size="3xs"
                        weight="400"
                        className={cn(
                            isFeatured && "xl:text-[21px]"
                        )}
                    >
                        for
                    </Heading>
                    {product.benefit && (
                        <Paragraph size="lg" className="hidden xl:block mb-1">{product.benefit}</Paragraph>
                    )}
                    <Paragraph>{product.description}</Paragraph>
                </div>
            </div>

            {product.badge && (
                <span
                    className={cn(
                        "absolute right-6 top-3 inline-flex h-[25px] items-center rounded-full",
                        "px-[13px] text-[12px] font-medium leading-none xl:hidden",
                        "max-[389px]:right-[18px]",
                        product.badgeTone === "blue"
                            ? "bg-white text-heroBlue"
                            : "bg-[#fffc85] text-gray500"
                    )}
                >
                    {product.badge}
                </span>
            )}

            <span className="absolute right-[clamp(10px,1.5vw,22px)] hidden size-5 items-center justify-center xl:flex">
                <span className="absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors group-hover:bg-trueBlack/25" />
                <CaretRightIcon
                    className="relative"
                    aria-hidden="true"
                    size={20}
                />
            </span>
            <ArrowRightIcon
                className="absolute bottom-[13px] right-7 xl:hidden"
                aria-hidden="true"
                size={20}
            />
        </Link>
    );
}
